/* eslint-disable import/no-cycle */
import React, { BaseSyntheticEvent, FC, useEffect, useMemo, useState } from 'react';
import { FormikProps } from 'formik';
import clsx from 'clsx';
import SchoolPicker from 'components/SchoolPicker';
import SelectField, { SelectFieldOption } from '../../../../FormFields/SelectField';
import { SignUpFormData, TRole } from '../../../../../models/auth';
import StateCityModel from '../../../../../models/StateCity';
import TextField from '../../../../FormFields/TextField';
import Typo from '../../../../Typo';
import { getFieldError } from '../../../../FormFields/Utils';
import { logger } from '../../../../../utils';
import styles from './personalInfo.module.sass';
import useSignUpActions from '../../useSignUpActions';

export interface PersonalInfoProps {
  firstNameKey: string;
  lastNameKey: string;
  schoolNameKey: string;
  industryNameKey: string;
  cityNameKey: string;
  stateNameKey: string;
  googleMode: boolean;
  formikProps: FormikProps<Partial<SignUpFormData>>;
}

type TSchool = {
  id: string;
  name: string;
  city: string;
  ncessch: number;
  nmcnty: string;
  state: string;
  street: string;
  zip: number;
  alias?: string;
};

export const optionRender = (option: TSchool) => (
  <div
    style={{ display: 'flex', flexDirection: 'column', padding: '5px 0' }}
    className={styles.option}
  >
    <div
      style={{
        fontSize: '14px',
        fontWeight: 'bold',
        textAlign: 'left',
      }}
    >
      {option.name}
      {option.alias && (
        <span style={{ fontStyle: 'italic', fontSize: '13px' }}>
          {' - '}({option.alias})
        </span>
      )}
    </div>
    <div
      style={{
        lineHeight: '1em',
        fontSize: '12px',
        textAlign: 'left',
      }}
    >
      {option.street}, {option.city}, {option.state} - {option.zip}
    </div>
  </div>
);

const PersonalInfo: FC<PersonalInfoProps> = props => {
  const {
    firstNameKey,
    lastNameKey,
    schoolNameKey,
    industryNameKey,
    cityNameKey,
    stateNameKey,
    formikProps,
  } = props;
  const { industries } = useSignUpActions();
  const errors = {
    firstName: getFieldError(firstNameKey, formikProps),
    lastName: getFieldError(lastNameKey, formikProps),
    school: getFieldError(schoolNameKey, formikProps),
    industry: getFieldError(industryNameKey, formikProps),
    city: getFieldError(cityNameKey, formikProps),
    state: getFieldError(stateNameKey, formikProps),
  };
  const [stateOptions, setStateOptions] = useState<SelectFieldOption[]>([]);
  const [cityOptions, setCityOptions] = useState<SelectFieldOption[]>([]);
  const [fetchingState, setFetchingState] = useState<boolean>();
  const [fetchingCities, setFetchingCities] = useState<boolean>();

  const selectedState = formikProps?.values?.state;

  useEffect(() => {
    async function fetchStateData() {
      try {
        setFetchingState(true);
        const response = (await StateCityModel.getUSStates()).data;
        const options = response.map(
          s =>
            ({
              id: s.id,
              name: s.name,
              value: s.name,
            }) as SelectFieldOption,
        );
        setStateOptions(options);
        setFetchingState(false);
      } catch (error) {
        setFetchingState(true);
        logger.error(error);
      }
    }
    fetchStateData();
  }, []);

  useEffect(() => {
    async function fetchStateCityData() {
      if (!selectedState) return;
      try {
        setFetchingCities(true);

        const {
          data: { cities },
        } = await StateCityModel.getUSStateCities(selectedState as string);
        const response = cities.map(
          s =>
            ({
              id: s.id,
              name: s.name,
              value: s.name,
            }) as SelectFieldOption,
        );
        setFetchingCities(false);
        setCityOptions(response);
        formikProps.setFieldValue('city', response.length > 0 ? '' : undefined);
      } catch (error) {
        setFetchingCities(true);
        logger.error(error);
      }
    }
    fetchStateCityData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedState]);

  const industryOptions = industries.map(i => ({ id: i, name: i, value: i }) as SelectFieldOption);

  const showSchoolField = useMemo(() => {
    if (fetchingState || fetchingCities) return false;

    if (stateOptions.length === 0) return false;

    if ((formikProps?.values?.state?.length || 0) === 0) return false;

    if (formikProps?.values?.state && cityOptions.length === 0) return true;

    if (cityOptions.length > 0 && formikProps?.values?.city?.length === 0) {
      return false;
    }

    return true;
  }, [
    fetchingCities,
    fetchingState,
    stateOptions,
    cityOptions.length,
    formikProps?.values?.city?.length,
    formikProps?.values?.state,
  ]);

  const handleStateChange = (any: BaseSyntheticEvent) => {
    formikProps.setFieldValue('state', any.target.value);
    formikProps.setFieldValue('city', '');
    formikProps.setFieldValue('organization', '');
    formikProps.setFieldValue('school_id', undefined);
  };

  return (
    <div className="pt-5 has-text-centered">
      <Typo normal bold classes={styles.fieldLabel}>
        What’s your name?
      </Typo>
      <div className={styles.nameFieldsContaier}>
        <TextField
          valueKey={firstNameKey}
          placeHolderColorBlack
          otherFieldProps={{
            placeholder: 'First Name',
            onChange: formikProps.handleChange,
            onBlur: formikProps.handleBlur,
            value: formikProps?.values?.first_name,
          }}
          error={errors.firstName}
        />
        <TextField
          valueKey={lastNameKey}
          placeHolderColorBlack
          otherFieldProps={{
            placeholder: 'Last Name',
            onChange: formikProps.handleChange,
            onBlur: formikProps.handleBlur,
            value: formikProps?.values?.last_name,
          }}
          error={errors.lastName}
        />
      </div>
      <Typo normal bold classes={styles.fieldLabel}>
        {formikProps?.values?.role === TRole.MENTOR
          ? 'What company do you work for?'
          : 'Where is your school or organization?'}
      </Typo>
      <div className={styles.otherInfoContainer}>
        {formikProps?.values?.role !== TRole.MENTOR && (
          <div className={styles.addressContainer}>
            <SelectField
              valueKey={stateNameKey}
              options={stateOptions}
              emptyOption="State"
              error={errors.state}
              loading={fetchingState}
              loadingMessage="Loading States"
              otherProps={{
                disabled: fetchingState,
                onChange: handleStateChange,
                onBlur: formikProps.handleBlur,
                value: formikProps?.values?.state,
              }}
            />

            <SelectField
              valueKey={cityNameKey}
              options={fetchingCities ? [] : cityOptions}
              emptyOption="Nearest Town/City"
              error={errors.city}
              loading={fetchingCities}
              loadingMessage={`Loading ${formikProps?.values?.state}'s Cities`}
              otherProps={{
                disabled: fetchingCities || !formikProps?.values?.state,
                onChange: formikProps.handleChange,
                onBlur: formikProps.handleBlur,
                value: formikProps?.values?.city,
              }}
              rootClassName={clsx(cityOptions?.length === 0 && 'is-invisible')}
            />
          </div>
        )}
        {(showSchoolField || formikProps?.values?.role === TRole.MENTOR) && (
          <div className={styles.schoolFieldContainer}>
            <div className={styles.schoolFieldLabelContainer}>
              <Typo normal bold classes={styles.fieldLabel}>
                Find your school or organization
              </Typo>
              <Typo normal classes={styles.fieldLabelDescription}>
                Begin typing the name of your school. Options will begin to appear after 3
                characters. When your school name appears, please select it.
              </Typo>
            </div>
            <SchoolPicker
              stateCode={formikProps?.values?.state ?? ''}
              fieldName={schoolNameKey}
              placeholder={
                formikProps?.values?.role === TRole.MENTOR
                  ? 'Company Name'
                  : 'School or Organization Name'
              }
              city={formikProps?.values?.city ?? ''}
              onChange={(isSchoolSelected, school) => {
                if (isSchoolSelected) {
                  formikProps.setFieldValue('organization', (school as TSchool).name);
                  formikProps.setFieldValue('school_id', (school as TSchool).id);
                } else {
                  formikProps.setFieldValue('organization', school as string);
                  formikProps.setFieldValue('school_id', null);
                }
              }}
              onBlur={formikProps.handleBlur}
              disabled={formikProps?.values?.state?.length === 0}
            />
            {formikProps?.values?.role === TRole.MENTOR && (
              <SelectField
                valueKey={industryNameKey}
                options={industryOptions}
                emptyOption="Industry"
                rootClassName={styles.schoolField}
                error={errors.industry}
                otherProps={{
                  onChange: formikProps.handleChange,
                  onBlur: formikProps.handleBlur,
                  value: formikProps?.values?.industry,
                }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonalInfo;
